/**
 * ACHIEVEMENTS -- edges from game events, fired at the shell and forgotten.
 *
 * The ids are OURS, locked in docs/ARCH-PLAN.md §4.4 and FOREVER: the
 * desktop shell maps them to whatever the current Steam app's schema calls
 * them (packages/desktop/src/steam.js -- identity under a real appid,
 * Valve's four stand-ins under Spacewar), so renaming one here orphans an
 * achievement players have already earned.  On the web there is no bridge
 * and every fire is `desktopBridge()?.unlockAchievement?.(id)` -- a no-op
 * by optional chaining, which is why call sites never ask first (§4.4's
 * posture, BP's achievements.ts adopted whole).
 *
 * THE TRIGGERS decide here, not at the call sites: the five emit lines in
 * app/loop.js and app/boot.js each hand over one observation (our
 * happiness, our pose, a bliss, a hunt moment, the roster) and this module
 * owns the edge -- threshold, dedupe, and the visited-places arithmetic --
 * so the conditions are one thing to read and one thing to test
 * (packages/desktop/test/steam.test.mjs drives them all).
 *
 * Deduped per session (the `fired` set) because the same edge can arrive
 * more than once and every fire crosses a process boundary; Steam dedupes
 * too (steam.js checks isActivated), this just keeps the IPC quiet.
 *
 * ALL_PLACES alone needs memory BETWEEN sessions -- thirteen places is an
 * afternoon, not a sitting -- and the server has none to offer: wants
 * regrow by design, so "want satisfied" cannot mean "ever visited", and
 * nothing in the protocol says "ever".  So the visited set keeps itself in
 * localStorage (the purr-mute's shelf, same hostile-storage posture:
 * every touch wrapped, absence degrades to a per-session set and the
 * achievement is simply earned the patient way).  Two of the thirteen
 * never arrive as a bliss -- happiness.js banks HUNT through the capture/
 * delivery paydays and SEEK through a find, neither via onBliss -- so the
 * hunt's delivery and any dug-up treasure feed the same set through their
 * own edges below.
 */

import { desktopBridge } from "./net/desktop.js";
import { getAdopting } from "./legacy-storage.js";
import { PURR_AT, MAX_CATS, PLACES } from "@catworld/shared";
import { CAR } from "@catworld/engine";

/** localStorage key for the visited-places set.  catworld.* like its
    shelf-mates (token, purr mute). */
const PLACES_KEY = "catworld.places";

/** What PLACES_KEY was called before the game was renamed; the first read
    adopts it, so nobody re-walks the street (legacy-storage.js). */
const LEGACY_PLACES_KEY = "lilysworld.places";

/**
 * The car's plan footprint and surface heights, for NIGHT_DRIVER ("sit
 * in the Tesla"): sitting anywhere ON the car -- sills to roof -- counts.
 * A rounded-outward envelope of the tier table (CAR_TIERS in
 * packages/engine/src/level/spec.js, importable since the car became
 * boxes; these numbers predate that and were traced off the same tiers):
 * z spans -2.38..2.42 of CAR.z, the widest tier is 1.86 m, and the
 * standable tops run 0.58 (sills) to 1.44 (roof).  The tolerances only
 * round outward; the driveway beside the car sits at y 0.016, far below
 * the floor here, so a cat sitting NEXT to the car can never read as
 * sitting on it.
 */
const CAR_HALF_W = 0.95;
const CAR_Z_NEAR = -2.4;
const CAR_Z_FAR = 2.45;
const CAR_TOP_MIN = 0.5;
const CAR_TOP_MAX = 1.6;

/** The thirteen ids ALL_PLACES counts -- exactly the PLACES table's. */
const PLACE_IDS = new Set(PLACES.map((p) => p.id));

/** Fired this session -- the IPC-quieting dedupe. */
/** @type {Set<string>} */
const fired = new Set();

/** localStorage, or null where even touching it throws (sandboxed
    iframes, some private modes) -- purr.js's posture, verbatim. */
function storage() {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
}

/** The visited-places set, loaded once and then held; a mangled or
    missing record is an empty set, never a throw (store.js's repair
    posture -- the next visit starts rewriting it whole). */
/** @type {Set<string> | null} */
let visited = null;

/** @returns {Set<string>} */
function visitedSet() {
  if (visited) return visited;
  visited = new Set();
  try {
    const raw = getAdopting(storage(), PLACES_KEY, LEGACY_PLACES_KEY);
    if (raw) {
      for (const id of JSON.parse(raw)) {
        if (typeof id === "string" && PLACE_IDS.has(id)) visited.add(id);
      }
    }
  } catch {
    // unreadable is simply "nothing visited yet"
  }
  return visited;
}

function saveVisited() {
  try {
    storage()?.setItem(PLACES_KEY, JSON.stringify([...visitedSet()]));
  } catch {
    // a set that cannot be remembered still counts this session
  }
}

/** @param {string} id */
function fire(id) {
  if (fired.has(id)) return;
  fired.add(id);
  desktopBridge()?.unlockAchievement?.(id);
}

/**
 * Every frame, from the loop's net step: our own happiness (off our own
 * snapshot row -- the server's word, never a local guess) and our pose.
 * FIRST_PURR is the meter crossing PURR_AT, the same edge that starts the
 * audible purr; NIGHT_DRIVER is sitting on the Tesla, checked here rather
 * than against the carbonnet dwell zone because the zone is the bonnet
 * disc and the trigger is the whole car.
 * @param {number} happy
 * @param {{ pos: { x: number, y: number, z: number }, sitting: boolean }} player
 */
export function achieveFrame(happy, player) {
  if (happy >= PURR_AT) fire("FIRST_PURR");
  if (player.sitting &&
      Math.abs(player.pos.x - CAR.x) <= CAR_HALF_W &&
      player.pos.z - CAR.z >= CAR_Z_NEAR && player.pos.z - CAR.z <= CAR_Z_FAR &&
      player.pos.y >= CAR_TOP_MIN && player.pos.y <= CAR_TOP_MAX) {
    fire("NIGHT_DRIVER");
  }
}

/**
 * One place banked by OUR cat -- a bliss broadcast (eleven of the
 * thirteen), the hunt's delivery, or a find, whichever edge saw it.
 * ALL_PLACES is the set reaching all thirteen.
 * @param {boolean} mine
 * @param {string} placeId
 */
export function achievePlace(mine, placeId) {
  if (!mine || !PLACE_IDS.has(placeId)) return;
  const v = visitedSet();
  if (v.has(placeId)) return;
  v.add(placeId);
  saveVisited();
  if (v.size === PLACE_IDS.size) fire("ALL_PLACES");
}

/**
 * A hunt moment (the `hunt` broadcast): "caught" out in the world is
 * FIRST_CATCH, "home" is the 2/3-delivery payday -- SPECIAL_DELIVERY, and
 * the day the hunt place counts as visited.
 * @param {boolean} mine
 * @param {string} kind
 */
export function achieveHunt(mine, kind) {
  if (!mine) return;
  if (kind === "caught") fire("FIRST_CATCH");
  if (kind === "home") {
    fire("SPECIAL_DELIVERY");
    achievePlace(true, "hunt");
  }
}

/**
 * A treasure dug up by our cat (the `found` message, re-finds included:
 * digging one out again is still digging one out) -- the seek place's
 * only edge.
 * @param {boolean} mine
 */
export function achieveFound(mine) {
  achievePlace(mine, "seek");
}

/**
 * The roster changed.  FULL_HOUSE is a full street -- sixteen cats,
 * whether the other fifteen are people or the AI keeping it alive; both
 * readings of §4.4's row ("15 others, or a full AI street") are the same
 * count, and the roster is the one list that holds it.
 * @param {number} count
 */
export function achieveRoster(count) {
  if (count >= MAX_CATS) fire("FULL_HOUSE");
}
